<template>
  <div>
    <OurGroups>
      <OurGroup>
        <OurInput
          v-model="form.name"
          :label="$t('label.title')"
          :placeholder="$t('placeholder.title')"
          :error="nameError"
        />

        <OurInput
          v-model="form.sort"
          from-one-to-hundred
          :label="$t('categories.position')"
          :placeholder="$t('placeholder.position')"
        />

        <OurSelect
          v-model="form.parentId"
          class="parent-category-select"
          :label="$t('categories.parentCategory')"
          :placeholder="$t('placeholder.selectParentCategory')"
          :options="parentCategories"
          item-label="name"
          value-key="id"
        />

        <OurMultiselect
          v-model="form.linkedProductCategoryIds"
          :label="$t('categories.linkedProductCategories')"
          :placeholder="$t('placeholder.selectLinkedProductCategories')"
          :options="hierarchicalLinkedProductCategories"
          item-label="description"
          value-key="id"
          :taggable="false"
          :disabled="loading"
          :hide-selected="false"
        />

        <OurCheckbox v-model="form.isActive" :label="$t('categories.active')" />

        <OurCheckbox
          v-model="form.forNotice"
          :label="$t('categories.canUse')"
          :disabled="isParentCategorySelected"
        />
      </OurGroup>
    </OurGroups>
  </div>
</template>

<script>
import { required, maxLength } from "vuelidate/lib/validators";
import ValidationService from "@/services/validation.service";
import { catalogCategoriesQuery } from "../index/gql/queries";
import apolloMixin from "@/mixins/apollo.mixin";
import PaginationSelect from "@/ui/select/PaginationSelect.vue";
import { productCategoriesQuery } from "../edit/gql/queries";

const { REGISTRATION_ADDITIONAL_TEXT_MAX_LENGTH } = ValidationService;

export default {
  name: "CategoriesForm",

  components: {
    PaginationSelect,
  },

  mixins: [apolloMixin],

  props: {
    value: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      form: {
        name: "",
        isActive: true,
        sort: 0,
        parentId: null,
        forNotice: false,
        linkedProductCategoryIds: [],
      },
      parentCategories: [],
      linkedProductCategories: [],
      loading: false,
    };
  },

  validations: {
    form: {
      name: { required, maxLength: maxLength(REGISTRATION_ADDITIONAL_TEXT_MAX_LENGTH) },
    },
  },

  computed: {
    isParentCategorySelected() {
      return Boolean(this.form.parentId);
    },

    nameError() {
      const isDirty = this.$v.form.name.$dirty;
      const isFilled = this.$v.form.name.required;

      const maxLengthError = this.$v.form.name.maxLength;

      let error = "";

      if (isDirty && !isFilled) {
        error = this.$t("validation.required");
      } else if (isDirty && !maxLengthError) {
        error = this.$t("validation.textMaxLength", {
          length: REGISTRATION_ADDITIONAL_TEXT_MAX_LENGTH,
        });
      }

      return error;
    },

    hierarchicalLinkedProductCategories() {
      return this.buildHierarchicalProductCategories(this.linkedProductCategories);
    },
  },

  watch: {
    value: {
      handler: "getForm",
      deep: true,
    },

    "form.parentId"(parentId) {
      if (parentId) {
        this.form.forNotice = false;
      }
    },
  },

  mounted() {
    this.getLinkedProductCategories();
    this.getParentCategories();
    this.getForm();
  },

  methods: {
    getForm() {
      if (this.value) {
        this.form = { ...this.value };

        if (this.form.parentId) {
          this.form.forNotice = false;
        }
      }
    },

    emitForm() {
      if (this.$v.$invalid) {
        this.$v.$touch();

        return false;
      }

      this.form.isValid = true;
      this.$emit("input", this.form);

      return true;
    },

    async getParentCategories() {
      const { catalogCategories } = await this.$get(
        catalogCategoriesQuery,
        {
          filters: {
            forSidebar: true,
          },
        },
        { withLoader: false }
      );

      const { data } = catalogCategories;

      this.parentCategories = data;

      if (this.$route.name === "CategoriesEdit") {
        this.parentCategories = this.parentCategories.map((category) => ({
          ...category,
          $isDisabled: category.id === this.$route.params.id || this.isChildOfCurrent(category.id),
        }));
      }
    },

    isChildOfCurrent(categoryId) {
      const currentCategoryId = this.$route.params.id;

      let current = this.parentCategories.find((c) => c.id === categoryId);

      while (current && current.parentId) {
        if (current.parentId === currentCategoryId) {
          return true;
        }

        current = this.parentCategories.find((c) => c.id === current.parentId);
      }

      return false;
    },

    async getLinkedProductCategories() {
      const { productCategories } = await this.$get(
        productCategoriesQuery,
        {},
        { withLoader: true }
      );

      this.linkedProductCategories = productCategories;
    },

    getProductCategoryParentId(category) {
      return category.parent?.id ?? category.parent ?? null;
    },

    buildHierarchicalProductCategories(categories) {
      if (!categories?.length) return [];

      const result = [];
      const visited = new Set();

      const getChildren = (parentId) =>
        categories.filter((item) => this.getProductCategoryParentId(item) === parentId);

      const traverse = (category, level) => {
        if (visited.has(category.id)) return;

        visited.add(category.id);
        result.push({ ...category, level });

        getChildren(category.id).forEach((child) => traverse(child, level + 1));
      };

      categories.forEach((category) => {
        if (!this.getProductCategoryParentId(category)) {
          traverse(category, 1);
        }
      });

      categories.forEach((category) => {
        if (!visited.has(category.id)) {
          traverse(category, 1);
        }
      });

      return result.map((category) => ({
        ...category,
        $isDisabled: category.catalogCategoryId !== null,
      }));
    },
  },
};
</script>

<style lang="postcss" scoped>
.parent-category-select {
  ::v-deep(.multiselect__option--disabled) {
    @apply !text-gray-400;
  }

  ::v-deep(.multiselect__option--disabled .select-label) {
    @apply !text-gray-400;
  }

  ::v-deep(.multiselect__option--disabled.multiselect__option--highlight),
  ::v-deep(.multiselect__option--disabled.multiselect__option--selected) {
    @apply !bg-transparent !text-gray-400;
  }
}
</style>
